(() => {
  if (window.RetroPartyDrawerLoader) return;

  const config = document.currentScript;
  const triggerSelector = '#cart-icon-bubble, [data-cart-drawer-open], [aria-controls="CartDrawer"]';
  const styleList = [config?.dataset.quantityPopoverCss, config?.dataset.componentCardCss].filter(Boolean);
  const sourceList = [
    config?.dataset.quantityInputSrc,
    config?.dataset.cartSrc,
    config?.dataset.quantityPopoverSrc,
    config?.dataset.cartUpsellSrc,
    config?.dataset.cartSyncSrc,
    config?.dataset.cartDrawerSrc,
    config?.dataset.cartRemoveSrc,
    config?.dataset.freeShippingSrc,
    config?.dataset.retropointsSrc,
  ].filter(Boolean);
  const stylePromises = new Map();
  const sourcePromises = new Map();
  let drawerPromise;
  let preloadListenersActive = true;

  const liveRegion = document.createElement('span');
  liveRegion.className = 'visually-hidden';
  liveRegion.setAttribute('role', 'status');
  liveRegion.setAttribute('aria-live', 'polite');
  liveRegion.hidden = true;
  document.body.appendChild(liveRegion);

  function absoluteUrl(src) {
    return new URL(src, document.baseURI).href;
  }

  function featureReady(src) {
    const pathname = new URL(src, document.baseURI).pathname;
    if (pathname.endsWith('/quantity-input.js')) return Boolean(customElements.get('quantity-input'));
    if (pathname.endsWith('/cart.js')) return Boolean(customElements.get('cart-items'));
    if (pathname.endsWith('/quantity-popover.js')) return Boolean(customElements.get('quantity-popover'));
    if (pathname.endsWith('/rp-cart-upsell.js')) return Boolean(window.RetroPartyCartUpsellLoaded);
    if (pathname.endsWith('/cart-drawer.js')) return Boolean(customElements.get('cart-drawer'));
    if (pathname.endsWith('/rp-cart-sync.js')) return Boolean(window.RetroPartyCartSync);
    if (pathname.endsWith('/rp-free-shipping.js')) return Boolean(window.RPFreeShipping);
    if (pathname.endsWith('/rp-retropoints.js')) return Boolean(window.RetroPartyPointsRedemption);
    if (pathname.endsWith('/rp-cart-remove-animation.js')) return Boolean(window.RetroPartyCartRemoveAnimation);
    return false;
  }

  function loadStyle(src) {
    const url = absoluteUrl(src);
    if (stylePromises.has(url)) return stylePromises.get(url);

    const promise = new Promise((resolve, reject) => {
      let link = [...document.querySelectorAll('link[rel="stylesheet"]')].find((candidate) => candidate.href === url);
      if (link?.sheet) {
        resolve();
        return;
      }

      const loaderOwned = !link;
      link ||= document.createElement('link');

      const cleanup = () => {
        link.removeEventListener('load', onLoad);
        link.removeEventListener('error', onError);
      };
      const onLoad = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        stylePromises.delete(url);
        if (loaderOwned) link.remove();
        reject(new Error(`Impossible de charger ${url}`));
      };

      link.addEventListener('load', onLoad, { once: true });
      link.addEventListener('error', onError, { once: true });
      if (loaderOwned) {
        link.rel = 'stylesheet';
        link.href = url;
        link.dataset.rpLoaderOwned = 'true';
        document.head.appendChild(link);
      } else {
        queueMicrotask(() => {
          if (link.sheet) onLoad();
        });
      }
    });

    stylePromises.set(url, promise);
    return promise;
  }

  function setLoading(loading, message = '') {
    document.querySelectorAll(triggerSelector).forEach((trigger) => {
      if (loading) trigger.setAttribute('aria-busy', 'true');
      else trigger.removeAttribute('aria-busy');
    });
    liveRegion.textContent = message;
    liveRegion.hidden = !message;
  }

  function loadScript(src) {
    const url = absoluteUrl(src);
    if (featureReady(url)) return Promise.resolve();
    if (sourcePromises.has(url)) return sourcePromises.get(url);

    const promise = new Promise((resolve, reject) => {
      let script = [...document.scripts].find((candidate) => candidate.src === url);
      if (script?.dataset.rpLoaded === 'true' || featureReady(url)) {
        resolve();
        return;
      }

      const loaderOwned = !script;
      script ||= document.createElement('script');

      const cleanup = () => {
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onError);
      };
      const onLoad = () => {
        script.dataset.rpLoaded = 'true';
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        sourcePromises.delete(url);
        if (loaderOwned) script.remove();
        reject(new Error(`Impossible de charger ${url}`));
      };

      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', onError, { once: true });
      if (loaderOwned) {
        script.src = url;
        script.dataset.rpLoaderOwned = 'true';
        document.head.appendChild(script);
      } else {
        queueMicrotask(() => {
          if (featureReady(url) || script.dataset.rpLoaded === 'true') onLoad();
        });
      }
    });

    sourcePromises.set(url, promise);
    return promise;
  }

  function removePreloadListeners() {
    if (!preloadListenersActive) return;
    preloadListenersActive = false;
    document.removeEventListener('pointerenter', preload, true);
    document.removeEventListener('focusin', preload, true);
    document.removeEventListener('touchstart', preload, true);
  }

  function ensureDrawer() {
    if (customElements.get('cart-drawer')) {
      removePreloadListeners();
      return Promise.resolve();
    }
    if (drawerPromise) return drawerPromise;

    setLoading(true, config?.dataset.loadingText || 'Chargement du panier…');
    drawerPromise = Promise.all(styleList.map((src) => loadStyle(src)))
      .then(() => sourceList.reduce((chain, src) => chain.then(() => loadScript(src)), Promise.resolve()))
      .then(() => {
        removePreloadListeners();
        setLoading(false);
      })
      .catch((error) => {
        drawerPromise = undefined;
        setLoading(false, config?.dataset.errorText || 'Le panier n’a pas pu être chargé. Réessayez.');
        throw error;
      });
    return drawerPromise;
  }

  function preload(event) {
    if (!event.target.closest(triggerSelector)) return;
    ensureDrawer().catch(() => {});
  }

  async function openDrawer(event) {
    const trigger = event.target.closest(triggerSelector);
    if (!trigger || customElements.get('cart-drawer')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    try {
      await ensureDrawer();
      document.querySelector('cart-drawer')?.open(trigger);
    } catch {
      window.location.assign(trigger.href || `${window.Shopify?.routes?.root || '/'}cart`);
    }
  }

  document.addEventListener('pointerenter', preload, true);
  document.addEventListener('focusin', preload, true);
  document.addEventListener('touchstart', preload, { capture: true, passive: true });
  document.addEventListener('click', openDrawer, true);

  window.RetroPartyDrawerLoader = { ensureDrawer };
})();
